import { useCallback, useEffect, useRef, useState } from 'react';
import { postAgentChat } from '../agent/api';
import { buildSystemPrompt } from '../agent/systemPrompt';
import {
  AGENT_STEP_LABELS,
  AGENT_TOOL_DECLARATIONS,
  dispatchTool,
} from '../agent/tools';
import type {
  AgentStatus,
  AgentUiMessage,
  GeminiContent,
  PendingConfirm,
  WizardField,
  WizardState,
} from '../agent/types';
import { formatWizardAnswer } from '../agent/wizard';
import {
  clearConversation,
  loadConversation,
  saveConversation,
} from '../agent/storage';

const MAX_LOOP = 8;

const uid = (() => {
  let n = 0;
  return () => `msg_${Date.now()}_${++n}`;
})();

const nowIso = () => new Date().toISOString();

function extractText(content: GeminiContent | undefined): string {
  if (!content) return '';
  return content.parts
    .map((p) => (typeof p.text === 'string' ? p.text : ''))
    .join('')
    .trim();
}

function extractFunctionCalls(content: GeminiContent | undefined) {
  if (!content) return [];
  return content.parts
    .filter((p) => p.functionCall && p.functionCall.name)
    .map((p) => p.functionCall!);
}

/**
 * Gemini の返答を contents に追加する前に、送信不能なパート（thought など）を除く。
 */
function sanitizeModelContent(content: GeminiContent): GeminiContent {
  return {
    role: content.role ?? 'model',
    parts: (content.parts || []).filter((p: any) => {
      if (p?.thought) return false;
      return p?.text || p?.functionCall || p?.functionResponse;
    }),
  };
}

/**
 * Gemini の turn 順序ルール:
 *   - functionCall を含む model turn の **直後** には functionResponse の user turn が必要
 *   - functionResponse の user turn の **直前** には functionCall の model turn が必要
 *
 * 上記を満たさない turn は drop する（末尾だけでなく中間にある孤立も除去）。
 * ウィザード/確認中にブラウザを閉じた localStorage 起因の破損状態を全クリーニングするため。
 */
function sanitizeContents(contents: GeminiContent[]): GeminiContent[] {
  if (!Array.isArray(contents) || contents.length === 0) return [];
  const valid: GeminiContent[] = [];
  for (let i = 0; i < contents.length; i++) {
    const cur = contents[i];
    if (!cur || !Array.isArray(cur.parts) || cur.parts.length === 0) continue;

    const hasFnCall = cur.role === 'model' && cur.parts.some((p: any) => p?.functionCall);
    const hasFnResp = cur.role === 'user' && cur.parts.some((p: any) => p?.functionResponse);

    if (hasFnCall) {
      const next = contents[i + 1];
      const nextHasResp = next?.role === 'user' && Array.isArray(next?.parts)
        && next.parts.some((p: any) => p?.functionResponse);
      if (!nextHasResp) continue; // 孤立 functionCall を捨てる
    }
    if (hasFnResp) {
      const prevValid = valid[valid.length - 1];
      const prevHasCall = prevValid?.role === 'model' && Array.isArray(prevValid?.parts)
        && prevValid.parts.some((p: any) => p?.functionCall);
      if (!prevHasCall) continue; // 直前に対応する call が無ければ捨てる
    }

    valid.push(cur);
  }
  return valid.map((c) => (c.role === 'model' ? sanitizeModelContent(c) : c));
}

/** turn 順序エラー判定（truncate されても拾えるよう緩めに） */
function isTurnOrderErrorMessage(msg: string): boolean {
  const m = String(msg || '');
  return /function response turn/i.test(m)
    || /turn comes immediately after a function/i.test(m)
    || /function call turn/i.test(m);
}

/** Gemini が `print(default_api.X)` のような擬似コードを吐いて壊れる既知バグ */
function isMalformedFunctionCallError(msg: string): boolean {
  return /MALFORMED_FUNCTION_CALL|Malformed function call/i.test(String(msg || ''));
}

/**
 * Gemini が finishReason: STOP かつ content.parts が空（中身なし）で返してくる既知バグ。
 * PHP 中継が 502 + "Empty Gemini response" でレスポンスする。
 * MALFORMED と同じくサンプリング揺らぎなので 1 回 retry で大抵成功する。
 */
function isEmptyGeminiResponseError(msg: string): boolean {
  return /Empty Gemini response/i.test(String(msg || ''));
}

/** retry 対象の transient エラー（同じ context で 1 回再試行すれば成功率が高いもの） */
function isTransientGeminiError(msg: string): boolean {
  return isMalformedFunctionCallError(msg) || isEmptyGeminiResponseError(msg);
}

export interface UseAgentReturn {
  uiMessages: AgentUiMessage[];
  status: AgentStatus;
  currentStep: string | null;
  pendingConfirm: PendingConfirm | null;
  wizard: WizardState | null;
  send: (text: string) => Promise<void>;
  resetConversation: () => void;
  resolveConfirm: (approved: boolean) => void;
  answerWizard: (value: any) => void;
}

export function useAgent(): UseAgentReturn {
  const [uiMessages, setUiMessages] = useState<AgentUiMessage[]>([]);
  const [status, setStatus] = useState<AgentStatus>('idle');
  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const wizardRef = useRef<WizardState | null>(null);
  useEffect(() => { wizardRef.current = wizard; }, [wizard]);

  // Gemini に送る contents（function call / response を含む完全履歴）
  const contentsRef = useRef<GeminiContent[]>([]);
  const busyRef = useRef(false);

  // 永続化の復元
  useEffect(() => {
    const stored = loadConversation();
    if (stored) {
      setUiMessages(stored.uiMessages);
      contentsRef.current = sanitizeContents(stored.contents);
    }
  }, []);

  // 永続化の保存（idle / error 時のみ。処理途中は保存せず「クリーンな境界」だけ永続化）
  useEffect(() => {
    if (status !== 'idle' && status !== 'error') return;
    saveConversation({ uiMessages, contents: contentsRef.current });
  }, [uiMessages, status]);

  const appendUiMessage = useCallback((msg: AgentUiMessage) => {
    setUiMessages((prev) => [...prev, msg]);
  }, []);

  const updateUiMessage = useCallback((id: string, patch: Partial<AgentUiMessage>) => {
    setUiMessages((prev) =>
      prev.map((m) => (m.id === id ? ({ ...m, ...patch } as AgentUiMessage) : m)),
    );
  }, []);

  const requestConfirm = useCallback(
    (args: Record<string, any>): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        const confirmId = uid();
        appendUiMessage({
          kind: 'confirm',
          id: confirmId,
          createdAt: nowIso(),
          args,
          state: 'pending',
        });
        setStatus('awaiting_confirm');
        setPendingConfirm({
          args,
          resolve: (approved: boolean) => {
            updateUiMessage(confirmId, {
              state: approved ? 'approved' : 'rejected',
            } as any);
            setPendingConfirm(null);
            resolve(approved);
          },
        });
      });
    },
    [appendUiMessage, updateUiMessage],
  );

  const requestUpdateConfirm = useCallback(
    (
      before: Record<string, any>,
      changes: Array<{ key: string; label: string; before: string; after: string }>,
    ): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        const confirmId = uid();
        appendUiMessage({
          kind: 'update_confirm',
          id: confirmId,
          createdAt: nowIso(),
          before,
          changes,
          state: 'pending',
        });
        setStatus('awaiting_confirm');
        setPendingConfirm({
          args: { _kind: 'update', before, changes },
          resolve: (approved: boolean) => {
            updateUiMessage(confirmId, {
              state: approved ? 'approved' : 'rejected',
            } as any);
            setPendingConfirm(null);
            resolve(approved);
          },
        });
      });
    },
    [appendUiMessage, updateUiMessage],
  );

  const resolveConfirm = useCallback(
    (approved: boolean) => {
      if (pendingConfirm) {
        pendingConfirm.resolve(approved);
      }
    },
    [pendingConfirm],
  );

  const requestWizard = useCallback(
    (fields: WizardField[]): Promise<Record<string, any>> => {
      if (!fields || fields.length === 0) {
        return Promise.resolve({});
      }
      return new Promise<Record<string, any>>((resolve) => {
        const first = fields[0];
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text: first.question,
        });
        setWizard({
          fields,
          currentIndex: 0,
          answers: {},
          resolve,
        });
        setStatus('awaiting_wizard');
        setCurrentStep(null);
      });
    },
    [appendUiMessage],
  );

  const answerWizard = useCallback(
    (value: any) => {
      const current = wizardRef.current;
      if (!current) return;
      const field = current.fields[current.currentIndex];
      const skipped = value === null || value === '' || value === undefined;
      const newAnswers = { ...current.answers };
      if (!skipped) newAnswers[field.key] = value;

      appendUiMessage({
        kind: 'user',
        id: uid(),
        createdAt: nowIso(),
        text: formatWizardAnswer(field, skipped ? null : value),
      });

      const nextIndex = current.currentIndex + 1;
      if (nextIndex >= current.fields.length) {
        current.resolve(newAnswers);
        setWizard(null);
        setStatus('calling_tool');
      } else {
        const nextField = current.fields[nextIndex];
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text: nextField.question,
        });
        setWizard({ ...current, currentIndex: nextIndex, answers: newAnswers });
      }
    },
    [appendUiMessage],
  );

  const resetConversation = useCallback(() => {
    if (pendingConfirm) {
      pendingConfirm.resolve(false);
    }
    if (wizardRef.current) {
      wizardRef.current.resolve({});
    }
    setPendingConfirm(null);
    setWizard(null);
    setCurrentStep(null);
    contentsRef.current = [];
    setUiMessages([]);
    setStatus('idle');
    clearConversation();
  }, [pendingConfirm]);

  const runLoop = useCallback(async () => {
    const systemInstruction = buildSystemPrompt();

    for (let i = 0; i < MAX_LOOP; i++) {
      setStatus('thinking');
      setCurrentStep('🤖 考え中…');

      // MALFORMED_FUNCTION_CALL / Empty Gemini response は Gemini 側のサンプリング揺らぎで
      // 起きる確率事象。同じ context で 1 回だけ再試行すると殆ど成功するので透過的に retry。
      const callChat = async () => postAgentChat({
        system_instruction: systemInstruction,
        contents: contentsRef.current,
        tools: [{ functionDeclarations: AGENT_TOOL_DECLARATIONS }],
      });

      let response;
      try {
        try {
          response = await callChat();
        } catch (innerErr: any) {
          if (isTransientGeminiError(innerErr?.message || '')) {
            // eslint-disable-next-line no-console
            console.warn('[agent] transient Gemini error → retry', innerErr?.message);
            response = await callChat();
          } else {
            throw innerErr;
          }
        }
        // success フィールドが false でも transient エラーの場合は同様に retry
        if ((!response.success || !response.content) && isTransientGeminiError(response.error || '')) {
          // eslint-disable-next-line no-console
          console.warn('[agent] transient Gemini error (response) → retry', response.error);
          response = await callChat();
        }
      } catch (e: any) {
        const msg = String(e?.message || e);
        if (isTurnOrderErrorMessage(msg)) {
          contentsRef.current = [];
          clearConversation();
          appendUiMessage({
            kind: 'error',
            id: uid(),
            createdAt: nowIso(),
            text: '会話履歴が不整合だったためリセットしました。もう一度メッセージを送ってください。',
          });
        } else if (isTransientGeminiError(msg)) {
          appendUiMessage({
            kind: 'error',
            id: uid(),
            createdAt: nowIso(),
            text: 'エージェントの応答が不安定でした（再試行も失敗）。もう一度メッセージを送ってください。',
          });
        } else {
          // 防御的: turn 順序エラー以外でも contents を sanitize しておき、
          // 永続化される localStorage に破損状態が残らないようにする
          contentsRef.current = sanitizeContents(contentsRef.current);
          appendUiMessage({
            kind: 'error',
            id: uid(),
            createdAt: nowIso(),
            text: `通信エラー: ${msg}`,
          });
        }
        setStatus('error');
        setCurrentStep(null);
        return;
      }

      if (!response.success || !response.content) {
        const errMsg = String(response.error || '不明なエラー');
        if (isTurnOrderErrorMessage(errMsg)) {
          contentsRef.current = [];
          clearConversation();
          appendUiMessage({
            kind: 'error',
            id: uid(),
            createdAt: nowIso(),
            text: '会話履歴が不整合だったためリセットしました。もう一度メッセージを送ってください。',
          });
        } else if (isTransientGeminiError(errMsg)) {
          appendUiMessage({
            kind: 'error',
            id: uid(),
            createdAt: nowIso(),
            text: 'エージェントの応答が不安定でした（再試行も失敗）。もう一度メッセージを送ってください。',
          });
        } else {
          contentsRef.current = sanitizeContents(contentsRef.current);
          appendUiMessage({
            kind: 'error',
            id: uid(),
            createdAt: nowIso(),
            text: `エージェントエラー: ${errMsg}`,
          });
        }
        setStatus('error');
        setCurrentStep(null);
        return;
      }

      const modelContent = sanitizeModelContent(response.content);
      if (modelContent.parts.length > 0) {
        contentsRef.current = [...contentsRef.current, modelContent];
      }

      const calls = extractFunctionCalls(modelContent);
      const text = extractText(modelContent);

      if (text) {
        appendUiMessage({
          kind: 'assistant',
          id: uid(),
          createdAt: nowIso(),
          text,
        });
      }

      if (calls.length === 0) {
        setStatus('idle');
        setCurrentStep(null);
        return;
      }

      // tool 呼び出しを順に実行し、結果を1つの content としてまとめる
      const responseParts: any[] = [];
      for (const call of calls) {
        const label = AGENT_STEP_LABELS[call.name]?.running || `🛠️ ${call.name} を実行中…`;
        setStatus('calling_tool');
        setCurrentStep(label);

        const activityId = uid();
        appendUiMessage({
          kind: 'activity',
          id: activityId,
          createdAt: nowIso(),
          toolName: call.name,
          args: call.args || {},
          status: 'running',
        });

        const startedAt = Date.now();
        try {
          const result = await dispatchTool(call.name, call.args || {}, { requestConfirm, requestUpdateConfirm, requestWizard });
          const durationMs = Date.now() - startedAt;
          updateUiMessage(activityId, {
            status: 'success',
            result,
            durationMs,
          } as any);
          responseParts.push({
            functionResponse: {
              name: call.name,
              response: { result },
            },
          });
        } catch (e: any) {
          const durationMs = Date.now() - startedAt;
          const message = e?.message || String(e);
          updateUiMessage(activityId, {
            status: 'error',
            errorMessage: message,
            durationMs,
          } as any);
          responseParts.push({
            functionResponse: {
              name: call.name,
              response: { error: message },
            },
          });
        }
      }

      contentsRef.current = [
        ...contentsRef.current,
        { role: 'user', parts: responseParts },
      ];
      // loop 継続
    }

    appendUiMessage({
      kind: 'error',
      id: uid(),
      createdAt: nowIso(),
      text: `処理が長時間化したため中断しました（最大${MAX_LOOP}ステップ）`,
    });
    setStatus('error');
    setCurrentStep(null);
  }, [appendUiMessage, requestConfirm, requestUpdateConfirm, requestWizard, updateUiMessage]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (busyRef.current) return;
      busyRef.current = true;

      appendUiMessage({
        kind: 'user',
        id: uid(),
        createdAt: nowIso(),
        text: trimmed,
      });

      // 破損状態のリカバリ: 末尾に孤立 functionCall があると Gemini に拒否されるので除く
      contentsRef.current = sanitizeContents(contentsRef.current);
      contentsRef.current = [
        ...contentsRef.current,
        { role: 'user', parts: [{ text: trimmed }] },
      ];

      try {
        await runLoop();
      } finally {
        busyRef.current = false;
      }
    },
    [appendUiMessage, runLoop],
  );

  return {
    uiMessages,
    status,
    currentStep,
    pendingConfirm,
    wizard,
    send,
    resetConversation,
    resolveConfirm,
    answerWizard,
  };
}
